import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Spinner } from "@/components/UIKit";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, Upload, Loader2, Send, Eye, RefreshCw, CheckCircle2, XCircle, FileCode2, X, KeyRound, Trash2,
} from "lucide-react";

const SLOT_LABELS = { auth: "Auth (autentisering)", sign: "Sign (signering)", enc: "Enc (kryptering)" };
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("nb-NO") : "–");
const btn = "h-9 px-3 rounded-md text-sm font-medium flex items-center gap-2 disabled:opacity-40 transition-colors";

function Check({ ok, label, testid }) {
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <div className="flex items-center gap-2 text-sm" data-testid={testid}>
      <Icon className={`h-4 w-4 ${ok ? "text-emerald-400" : "text-zinc-600"}`} />
      <span className={ok ? "text-zinc-200" : "text-zinc-500"}>{label}</span>
    </div>
  );
}

function StatusCard({ st }) {
  const certs = st.certificates || {};
  return (
    <Card testid="mt1-status-card">
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500">Miljø</div>
            <div className="text-xl font-semibold text-zinc-100" data-testid="mt1-environment">{st.environment}</div>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs ${st.ready_to_send ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`} data-testid="mt1-ready-badge">
            {st.ready_to_send ? "Klar til sending" : "Mangler gyldig signeringssertifikat"}
          </span>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          <Check ok={!!st.sender_gln} label={`Avsender-GLN: ${st.sender_gln || "mangler"}`} testid="mt1-check-gln" />
          <Check ok label={`Elhub-GLN: ${st.recipient_gln}`} testid="mt1-check-elhub-gln" />
          <Check ok={!!st.elhub_public_keys?.test} label={`Elhub test-nøkkel (til ${fmtDate(st.elhub_public_keys?.test?.not_after)})`} testid="mt1-check-pub-test" />
          <Check ok={!!st.elhub_public_keys?.prod} label={`Elhub prod-nøkkel (til ${fmtDate(st.elhub_public_keys?.prod?.not_after)})`} testid="mt1-check-pub-prod" />
          {Object.keys(SLOT_LABELS).map((s) => (
            <Check key={s} ok={!!certs[s]?.matches_expected} label={`${SLOT_LABELS[s]}: ${certs[s] ? "lastet opp" : "mangler .p12"}`} testid={`mt1-check-cert-${s}`} />
          ))}
        </div>
      </div>
    </Card>
  );
}

function ThumbprintCard({ st, reload }) {
  const [tp, setTp] = useState(st.expected_thumbprints || {});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put("/elhub/mt1/thumbprints", tp); toast.success("Thumbprints lagret"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setBusy(false); }
  };
  return (
    <Card testid="mt1-thumbprint-card">
      <div className="p-5 space-y-3">
        <div className="flex items-center gap-2 text-zinc-200 font-medium"><KeyRound className="h-4 w-4 text-emerald-400" /> Commfides thumbprints (SHA1)</div>
        <p className="text-xs text-zinc-500">Brukes til å kontrollere at riktig .p12-fil lastes opp. Selve filen trengs for å signere meldingene.</p>
        {Object.keys(SLOT_LABELS).map((s) => (
          <div key={s}>
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{SLOT_LABELS[s]}</label>
            <input value={tp[s] || ""} onChange={(e) => setTp({ ...tp, [s]: e.target.value })}
              className="w-full h-9 px-3 rounded-md bg-zinc-950 border border-zinc-800 text-xs font-mono text-zinc-200" data-testid={`mt1-thumbprint-${s}`} />
          </div>
        ))}
        <button onClick={save} disabled={busy} className={`${btn} border border-zinc-800 hover:bg-zinc-900 text-zinc-200`} data-testid="mt1-thumbprint-save">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Lagre thumbprints
        </button>
      </div>
    </Card>
  );
}

function CertUploadCard({ st, reload }) {
  const [file, setFile] = useState(null);
  const [pw, setPw] = useState("");
  const [slot, setSlot] = useState("");
  const [busy, setBusy] = useState(false);
  const upload = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("password", pw); fd.append("slot", slot);
      const { data } = await api.post("/elhub/mt1/certificate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Lagret som ${SLOT_LABELS[data.slot]}${data.matches_expected ? " – thumbprint stemmer" : ""}`);
      setFile(null); setPw(""); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opplasting feilet"); }
    finally { setBusy(false); }
  };
  const remove = async (s) => {
    try { await api.delete(`/elhub/mt1/certificate/${s}`); toast.success("Fjernet"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
  };
  const certs = st.certificates || {};
  return (
    <Card testid="mt1-cert-upload-card">
      <div className="p-5 space-y-3">
        <div className="flex items-center gap-2 text-zinc-200 font-medium"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Last opp sertifikat (.p12 / .pfx)</div>
        <label className="h-10 px-3 rounded-md border border-dashed border-zinc-700 hover:border-emerald-500/50 text-sm text-zinc-400 flex items-center gap-2 cursor-pointer">
          <Upload className="h-4 w-4" /> <span className="truncate" data-testid="mt1-cert-filename">{file ? file.name : "Velg fil"}</span>
          <input type="file" accept=".p12,.pfx" className="hidden" data-testid="mt1-cert-file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <input type="password" placeholder="Passord til sertifikatet" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password"
          className="w-full h-9 px-3 rounded-md bg-zinc-950 border border-zinc-800 text-sm text-zinc-200" data-testid="mt1-cert-password" />
        <select value={slot} onChange={(e) => setSlot(e.target.value)} className="w-full h-9 px-2 rounded-md bg-zinc-950 border border-zinc-800 text-sm text-zinc-200" data-testid="mt1-cert-slot">
          <option value="">Finn type automatisk (via thumbprint)</option>
          {Object.entries(SLOT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={upload} disabled={!file || busy} className={`${btn} bg-emerald-500 hover:bg-emerald-400 text-black`} data-testid="mt1-cert-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Last opp
        </button>
        <div className="space-y-1.5 pt-2 border-t border-zinc-900">
          {Object.entries(certs).filter(([, v]) => v).map(([s, c]) => (
            <div key={s} className="flex items-center justify-between text-xs" data-testid={`mt1-cert-row-${s}`}>
              <span className="text-zinc-400"><span className="text-zinc-200">{SLOT_LABELS[s]}</span> · {c.filename} · gyldig til {fmtDate(c.not_after)}</span>
              <button onClick={() => remove(s)} className="text-zinc-500 hover:text-red-400" data-testid={`mt1-cert-delete-${s}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

const DEFAULT_PARAMS = { mpid: "", date: new Date().toISOString().slice(0, 10), customer_kind: "privat", customer_id: "", given_name: "", family_name: "", customer_name: "", street_name: "", building_number: "", postcode: "", city: "", email: "", query_type: "MDMP" };

function Field({ k, label, params, set, type = "text" }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
      <input type={type} value={params[k] || ""} onChange={(e) => set({ ...params, [k]: e.target.value })}
        className="w-full h-9 px-3 rounded-md bg-zinc-950 border border-zinc-800 text-sm text-zinc-200" data-testid={`mt1-param-${k}`} />
    </div>
  );
}

function CustomerFields({ params, set }) {
  const org = params.customer_kind === "bedrift";
  return (
    <>
      <div>
        <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Kundetype</label>
        <select value={params.customer_kind} onChange={(e) => set({ ...params, customer_kind: e.target.value })}
          className="w-full h-9 px-2 rounded-md bg-zinc-950 border border-zinc-800 text-sm text-zinc-200" data-testid="mt1-param-customer_kind">
          <option value="privat">Privat (fødselsnummer)</option>
          <option value="bedrift">Bedrift (orgnr)</option>
        </select>
      </div>
      <Field k="customer_id" label={org ? "Organisasjonsnummer" : "Fødselsnummer"} params={params} set={set} />
      {org ? <Field k="customer_name" label="Firmanavn" params={params} set={set} /> : (
        <><Field k="given_name" label="Fornavn" params={params} set={set} /><Field k="family_name" label="Etternavn" params={params} set={set} /></>
      )}
      <Field k="street_name" label="Gate" params={params} set={set} />
      <Field k="building_number" label="Husnr." params={params} set={set} />
      <Field k="postcode" label="Postnr." params={params} set={set} />
      <Field k="city" label="Sted" params={params} set={set} />
      <Field k="email" label="E-post (valgfri)" params={params} set={set} />
    </>
  );
}

function RunnerCard({ cases, ready, onRun, onPreview }) {
  const [caseId, setCaseId] = useState("brs-101");
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [busy, setBusy] = useState("");
  const c = cases.find((x) => x.id === caseId);
  const go = async (kind) => {
    setBusy(kind);
    try { await (kind === "run" ? onRun : onPreview)({ case_id: caseId, params }); }
    finally { setBusy(""); }
  };
  if (!c) return null;
  return (
    <Card testid="mt1-runner-card">
      <div className="p-5 space-y-4">
        <div className="flex items-center gap-2 text-zinc-200 font-medium"><Send className="h-4 w-4 text-emerald-400" /> Kjør testcase</div>
        <div className="flex flex-wrap gap-2">
          {cases.map((x) => (
            <button key={x.id} onClick={() => setCaseId(x.id)} data-testid={`mt1-case-${x.id}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${x.id === caseId ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "border-zinc-800 text-zinc-400 hover:bg-zinc-900"}`}>
              <span className="font-mono mr-1.5">{x.brs}</span>{x.title}
            </button>
          ))}
        </div>
        <div className="text-xs text-zinc-500" data-testid="mt1-case-meta">Melding: <span className="font-mono text-zinc-300">{c.message}</span> · Dokumenttype {c.document_type}</div>
        {c.fields.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {c.fields.includes("mpid") && <Field k="mpid" label="Målepunkt-ID (GSRN, 18 siffer)" params={params} set={setParams} />}
            {c.fields.includes("date") && <Field k="date" label="Dato" type="date" params={params} set={setParams} />}
            {c.fields.includes("query_type") && <Field k="query_type" label="Spørringstype (MDMP/MDCU)" params={params} set={setParams} />}
            {c.fields.includes("customer") && <CustomerFields params={params} set={setParams} />}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => go("preview")} disabled={!!busy} className={`${btn} border border-zinc-800 hover:bg-zinc-900 text-zinc-200`} data-testid="mt1-preview-btn">
            {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Forhåndsvis XML
          </button>
          <button onClick={() => go("run")} disabled={!!busy || !ready} className={`${btn} bg-emerald-500 hover:bg-emerald-400 text-black`} data-testid="mt1-run-btn">
            {busy === "run" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send til Elhub
          </button>
        </div>
      </div>
    </Card>
  );
}

function XmlDialog({ data, onClose }) {
  if (!data) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="mt1-xml-dialog">
      <div className="w-full max-w-4xl max-h-[85vh] flex flex-col rounded-xl border border-zinc-800 bg-[#0b0b0b]">
        <div className="p-4 border-b border-zinc-900 flex items-center justify-between gap-3">
          <div className="text-sm text-zinc-300 flex flex-wrap items-center gap-3">
            <FileCode2 className="h-4 w-4 text-emerald-400" />
            {data.schema_valid !== undefined && <span data-testid="mt1-xml-schema" className={data.schema_valid ? "text-emerald-400" : "text-red-400"}>{data.schema_valid ? "Gyldig mot Elhub XSD" : "XSD-feil"}</span>}
            {data.signed !== undefined && <span data-testid="mt1-xml-signed" className={data.signed ? "text-emerald-400" : "text-amber-400"}>{data.signed ? `Signert (${data.signature_check?.detail})` : "Ikke signert – sertifikat mangler"}</span>}
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200" data-testid="mt1-xml-close"><X className="h-5 w-5" /></button>
        </div>
        {data.schema_errors?.length > 0 && <div className="px-4 pt-3 text-xs text-red-400">{data.schema_errors.join(" · ")}</div>}
        <pre className="flex-1 overflow-auto p-4 text-[11px] leading-relaxed text-zinc-300 font-mono whitespace-pre" data-testid="mt1-xml-content">{data.xml}</pre>
      </div>
    </div>
  );
}

function RunRow({ r, onOpen, onAck }) {
  return (
    <tr className="border-t border-zinc-900 align-top" data-testid={`mt1-run-row-${r.id}`}>
      <td className="py-2.5 pr-3 text-zinc-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString("nb-NO")}</td>
      <td className="py-2.5 pr-3 font-mono text-zinc-300">{r.brs}</td>
      <td className="py-2.5 pr-3 text-zinc-400">{r.message}</td>
      <td className="py-2.5 pr-3">
        <span className={r.ok ? "text-emerald-400" : "text-red-400"}>{r.http_status ?? "–"} {r.ok ? "OK" : "Feil"}</span>
        {r.fault && <div className="text-red-400/80 max-w-xs truncate" title={r.fault}>{r.fault}</div>}
        {r.items?.map((it) => (
          <div key={it.document_id} className="text-zinc-400 flex items-center gap-2 mt-1">
            <span>{it.message} {it.mpid ? `· ${it.mpid}` : ""}</span>
            <button onClick={() => onAck(it.document_id)} className="text-emerald-400 hover:underline" data-testid={`mt1-ack-${it.document_id}`}>Kvitter</button>
          </div>
        ))}
      </td>
      <td className="py-2.5 text-right"><button onClick={() => onOpen(r.id)} className="text-zinc-400 hover:text-zinc-100" data-testid={`mt1-run-open-${r.id}`}>Vis</button></td>
    </tr>
  );
}

function RunsCard({ runs, reload, onOpen, onAck }) {
  return (
    <Card testid="mt1-runs-card">
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-zinc-200 font-medium">Kjørte tester</div>
          <button onClick={reload} className="text-zinc-500 hover:text-zinc-200" data-testid="mt1-runs-refresh"><RefreshCw className="h-4 w-4" /></button>
        </div>
        {runs.length === 0 ? <div className="text-sm text-zinc-500 py-6" data-testid="mt1-runs-empty">Ingen tester er sendt ennå.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs"><tbody>{runs.map((r) => <RunRow key={r.id} r={r} onOpen={onOpen} onAck={onAck} />)}</tbody></table>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function ElhubMT1() {
  const [st, setSt] = useState(null);
  const [cases, setCases] = useState([]);
  const [runs, setRuns] = useState([]);
  const [xml, setXml] = useState(null);

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([api.get("/elhub/mt1/status"), api.get("/elhub/mt1/testcases"), api.get("/elhub/mt1/runs")]);
    setSt(a.data); setCases(b.data); setRuns(c.data);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Klarte ikke å hente MT1-status")); }, [load]);

  const preview = async (body) => {
    try { const { data } = await api.post("/elhub/mt1/preview", body); setXml(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
  };
  const run = async (body) => {
    try {
      const { data } = await api.post("/elhub/mt1/run", body);
      data.ok ? toast.success(`Sendt – Elhub svarte ${data.http_status}`) : toast.error(data.fault || `Elhub svarte ${data.http_status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sending feilet"); }
  };
  const ack = async (id) => {
    try { await api.post("/elhub/mt1/acknowledge", { original_document_id: id }); toast.success("Kvittering sendt"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
  };
  const open = async (id) => {
    const { data } = await api.get(`/elhub/mt1/runs/${id}`);
    setXml({ xml: `<!-- FORESPØRSEL -->\n${data.request_xml}\n\n<!-- SVAR FRA ELHUB (${data.http_status ?? "ingen"}) -->\n${data.response_xml || data.fault || ""}` });
  };

  if (!st) return <Spinner label="Henter MT1-status" />;
  return (
    <div className="space-y-6" data-testid="elhub-mt1-page">
      <PageHeader title="Elhub MT1-testkjøring" subtitle="Krafthub sender signerte BRS-meldinger direkte til Elhub. Ediel registrerer resultatet automatisk." testid="mt1-header" />
      {!st.ready_to_send && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 text-sm text-amber-200" data-testid="mt1-missing-cert-alert">
          <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
          Last opp Commfides-sertifikatet (.p12/.pfx) med passord for å kunne sende. Du kan forhåndsvise meldingene allerede nå.
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2"><StatusCard st={st} /></div>
        <CertUploadCard st={st} reload={load} />
      </div>
      <RunnerCard cases={cases} ready={st.ready_to_send} onRun={run} onPreview={preview} />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2"><RunsCard runs={runs} reload={load} onOpen={open} onAck={ack} /></div>
        <ThumbprintCard key={JSON.stringify(st.expected_thumbprints)} st={st} reload={load} />
      </div>
      <XmlDialog data={xml} onClose={() => setXml(null)} />
    </div>
  );
}
